import { Button } from '@/components/ui/button';
import { Ellipsis } from '@/components/ui/ellipsis';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/utils/classUtils';
import { Check, ChevronsUpDown } from 'lucide-react';
import { useState, type FC } from 'react';
import fdaLogo from '@/assets/fda-logo.png';
// isoLogo import removed — type is now on reference, not source
import euLogo from '@/assets/eu-logo.png';

const SOURCE_LOGOS: Record<string, string> = {
  fda_guidance: fdaLogo,
  fda_21_cfr: fdaLogo,
  eu_mdr: euLogo,
  eu_ivdr: euLogo,
  mdcg: euLogo,
};

const badgeClassName =
  'inline-flex items-center gap-[6px] px-[8px] py-[3px] rounded-[8px] text-[12px] font-medium text-[#171717] tracking-[0.18px] bg-[#FAFAFA] border border-solid border-[#E5E5E5] cursor-pointer hover:bg-neutral-100 transition-colors';

interface SourceComboboxProps {
  sources: { id: string; title: string }[];
  value: string;
  onChange: (value: string) => void;
  /** Use badge-style trigger matching the feed table */
  badgeTrigger?: boolean;
}

export const SourceCombobox: FC<SourceComboboxProps> = ({
  sources,
  value,
  onChange,
  badgeTrigger = false,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const selected = sources.find(s => s.id === value);
  const selectedLabel = selected?.title;

  const filtered = search.trim()
    ? sources.filter(s => s.title.toLowerCase().includes(search.toLowerCase()))
    : sources;

  const renderBadgeTrigger = () => {
    if (!selected) {
      return (
        <span className={cn(badgeClassName, 'text-muted-foreground')}>
          Select source
          <ChevronsUpDown className="h-3 w-3 shrink-0 opacity-50" />
        </span>
      );
    }
    const logo = SOURCE_LOGOS[selected.id];
    const displayTitle = selected.title;
    return (
      <span className={badgeClassName}>
        {logo && (
          <img src={logo} alt="" className="w-[16px] h-[16px] object-contain" />
        )}
        <Ellipsis lineClamp={1} hasTooltip>
          {displayTitle}
        </Ellipsis>
        <ChevronsUpDown className="h-3 w-3 shrink-0 opacity-50" />
      </span>
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        {badgeTrigger ? (
          <button type="button" role="combobox" aria-expanded={open}>
            {renderBadgeTrigger()}
          </button>
        ) : (
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between font-normal"
          >
            {selectedLabel ?? (
              <span className="text-muted-foreground">Select source</span>
            )}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0"
        onOpenAutoFocus={e => e.preventDefault()}
      >
        <div className="flex flex-col">
          <div className="border-b">
            <input
              type="text"
              placeholder="Search sources..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full bg-transparent px-3 py-2 text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>
          <div className="max-h-[280px] overflow-y-auto py-1">
            {filtered.length === 0 && (
              <div className="text-muted-foreground py-4 text-center text-sm">
                No source found.
              </div>
            )}
            {filtered.map(s => (
              <button
                key={s.id}
                onClick={() => {
                  onChange(s.id);
                  setOpen(false);
                  setSearch('');
                }}
                className={cn(
                  'flex w-full items-center gap-2 px-3 py-1.5 text-sm',
                  'hover:bg-accent focus-visible:outline-none focus-visible:bg-accent'
                )}
              >
                <Check
                  className={cn(
                    'h-4 w-4 shrink-0',
                    value === s.id ? 'opacity-100' : 'opacity-0'
                  )}
                />
                <span className="truncate">{s.title}</span>
              </button>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
